import { normalisedOperation } from "../lib/deterministicSteps";

/**
 * One filled shape of a glyph, as Cascade draws it.
 *
 * `evenOdd` carries the fill rule the source sets on the shapes that need it. A
 * shape drawn under the default rule where its author asked for even-odd loses
 * its holes and fills in solid, so the flag travels with the path rather than
 * being inferred from it.
 */
interface GlyphShape {
  d: string;
  evenOdd?: boolean;
}

/**
 * A glyph and the box its coordinates are in.
 *
 * The box is per-glyph because the source's are not uniform — most are 24×24 and
 * at least one is 26×26 — and a path drawn against the wrong box is silently
 * mis-scaled rather than visibly broken.
 */
interface CascadeGlyph {
  viewBox: string;
  shapes: GlyphShape[];
}

/**
 * Cascade's own glyph for each operation it declares one for.
 *
 * **These are the engine's pictures, carried over rather than approximated.**
 * The auditor reading a step row here is often the same person who opens the
 * workflow in Optro Analytics, where Cascade draws these exact shapes against
 * these exact operation ids; a lookalike chosen from a general icon set would be
 * Midship inventing a second iconography for a vocabulary it does not own, and
 * the two would read as two different operations the first time a glyph was
 * picked differently from Cascade's.
 *
 * **Keyed on Cascade's real ids, not on what any one run happens to log.** The
 * ids here are the operation ids Cascade declares, so a key that no step ever
 * arrives under is a key Cascade has retired rather than one this map invented —
 * and an id arriving with no glyph falls through to the neutral one instead of
 * being aliased onto a near-neighbour. An alias would be this build asserting
 * that two operations are one, which is a claim only the engine can make.
 *
 * The paths are the source's, unedited. The only thing not carried shape by
 * shape is `fill="currentColor"`, which every one of them sets and which the
 * element below sets once for all of them, so the glyph takes the colour of the
 * text it sits beside.
 *
 * Re-derive by taking the glyph each `@Operation.register`-decorated class under
 * Cascade's `apps/operations/` draws itself with, keyed by that class's `id` —
 * the same walk the operation vocabulary itself is derived by, so a glyph and
 * the id it hangs on are read off one class rather than paired by hand here.
 */
const GLYPHS: Record<string, CascadeGlyph> = {
  join: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m9 3v9 9h2v-18zm0 9-4-4v3h-3v2h3v3zm4-9v18h2v-9-9zm2 9 4 4v-3h3v-2h-3v-3z",
      },
    ],
  },
  addcolumns: {
    viewBox: "0 0 26 26",
    shapes: [
      {
        d: "M 4.1875 0 C 3.585938 0 3.1875 0.398438 3.1875 1 L 3.1875 25 C 3.1875 25.601563 3.585938 26 4.1875 26 L 16.1875 26 C 16.789063 26 17.1875 25.601563 17.1875 25 L 17.1875 21.59375 C 16.488281 21.394531 15.789063 21.210938 15.1875 20.8125 L 15.1875 24 L 5.1875 24 L 5.1875 2 L 15.1875 2 L 15.1875 5.1875 C 15.789063 4.886719 16.488281 4.601563 17.1875 4.5 L 17.1875 1 C 17.1875 0.398438 16.789063 0 16.1875 0 Z M 19.1875 6.1875 C 15.386719 6.1875 12.40625 9.199219 12.40625 13 C 12.40625 16.800781 15.488281 19.8125 19.1875 19.8125 C 22.886719 19.8125 26 16.699219 26 13 C 26 9.300781 22.988281 6.1875 19.1875 6.1875 Z M 18.1875 9 L 20.1875 9 L 20.1875 12 L 23.1875 12 L 23.1875 14 L 20.1875 14 L 20.1875 17 L 18.1875 17 L 18.1875 14 L 15.1875 14 L 15.1875 12 L 18.1875 12 Z",
      },
    ],
  },
  filter: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m3 3v2h1l5 8v8h6v-8l5-8h1v-2zm3.35938 2h11.28322l-1.25 2h-8.78322zm2.5 4h6.28322l-2.1426 3.4258v6.5742h-2v-6.5742z",
      },
    ],
  },
  validation: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m8 2c-1.64497 0-3 1.35503-3 3v11h-3v3c0 1.645 1.35503 3 3 3h9 1c1.645 0 3-1.355 3-3v-11h4v-3c0-1.64497-1.355-3-3-3zm0 2h8.1875c-.1136.31489-.1875.64816-.1875 1v14c0 .5602-.4284.9907-.9863.998v-.0117c-.5635.0076-1.006-.4232-1.0137-.9863l-.0273-2.9961-.9727.0098v-.0137h-6v-11c0-.56503.43497-1 1-1zm11 0c.565 0 1 .43497 1 1v1h-2v-1c0-.56503.435-1 1-1zm-5.4648 4.05078-2.5352 2.53512-1.51758-1.51754-1.41406 1.41404 2.93164 2.9317 3.9492-3.94926zm-9.5352 9.94922h7.9902l.0098 1.0254v.0019c.0047.3426.0812.6664.1934.9727h-7.1934c-.56503 0-1-.435-1-1z",
      },
    ],
  },
  dedupe: {
    viewBox: "0 0 24 24",
    shapes: [
      { d: "M4 2C2.895 2 2 2.895 2 4V18H4V4H18V2H4Z" },
      {
        d: "M8 6C6.895 6 6 6.895 6 8V20C6 21.105 6.895 22 8 22H14V20H8V8H20V14H22V8C22 6.895 21.105 6 20 6H8Z",
      },
      {
        d: "M17.4238 16.0098L16.0098 17.4238L18.5918 20.0059L16.0098 22.5859L17.4238 24L20.0059 21.4199L22.5859 24L24 22.5859L21.4199 20.0059L24 17.4238L22.5859 16.0098L20.0059 18.5918L17.4238 16.0098Z",
      },
    ],
  },
  pivot: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m5 3c-1.09306 0-2 .90694-2 2v14c0 1.0931.90694 2 2 2h14c1.0931 0 2-.9069 2-2v-14c0-1.09306-.9069-2-2-2zm0 2h2v2h-2zm4 0h10v2h-10zm-4 4h2v10h-2zm4 0h10v4 6h-6-4zm4 10v-6l-3 3zm0-6h6l-3-3z",
      },
    ],
  },
  sample: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m18.5 2.00586c-.8978.00002-1.7955.34038-2.4746 1.01953l-2.4746 2.47461c-.0934-.09603-.2052-.17231-.3287-.22432-.1234-.052-.2561-.07867-.3901-.07841-.1987.00044-.3929.06009-.5576.17136-.1647.11126-.2925.26908-.3671.45331s-.0927.38652-.0518.58104.1388.37245.2812.51108l-3.80272 3.80274c-.99461.994-1.26352 2.1872-1.46093 3.0996-.19742.9124-.33738 1.5268-.8086 1.9981-.18746.1875-.29277.4418-.29277.707s.10531.5195.29277.707l.70703.707c.18755.1875.44186.2928.70704.2928.26517 0 .51948-.1053.70703-.2928.47121-.4712 1.083-.6116 1.98435-.8007.9014-.1892 2.0829-.4371 3.0567-1.4102l3.8593-3.8613h.002c.0906.1015.201.1834.3245.2407.1234.0574.2572.0889.3932.0928.136.0038.2714-.0202.3978-.0705.1265-.0503.2413-.1258.3376-.2221.0962-.0962.1718-.211.2221-.3375.0503-.1264.0742-.2618.0704-.3978s-.0354-.2699-.0927-.3933c-.0574-.1234-.1393-.2338-.2408-.3244v-.002l2.4766-2.47459c1.2623-1.26361 1.3002-3.25595.2148-4.625-.0497-.12109-.1227-.23125-.2148-.32422-.6796-.67885-1.5788-1.01955-2.4766-1.01953zm0 1.98828c.382.00002.765.14817 1.0625.44531.5937.59371.5943 1.52619 0 2.1211l-2.4766 2.47461-2.1211-2.1211 2.4747-2.47461c.2968-.29685.6785-.44533 1.0605-.44531zm-4.9492 4.33398 2.1211 2.12108-3.8594 3.8613c-.5302.5299-1.1636.6806-2.05273.8672-.3827.0803-.82624.1877-1.26954.334.14764-.4441.25465-.8887.33789-1.2734.19309-.8924.35254-1.5404.91993-2.1074zm-11.5508-1.32812v15h15v-6.4062h-2v4.4062h-11v-11h4.03125v-2z",
      },
    ],
  },
  unpivot: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m5 3c-1.09306 0-2 .90694-2 2v14c0 1.0931.90694 2 2 2h6v-2h-2v-10h10v7h2v-11c0-1.09306-.9069-2-2-2zm0 2h2v2h-2zm4 0h10v2h-10zm-4 4h2v10h-2z",
        evenOdd: true,
      },
      {
        d: "m13 19 4-4v3h2c1.729 0 5 1.045 5 5v1h-2v-1c0-2.823-2.5039-2.994-3.0059-3h-1.9941v3z",
      },
    ],
  },
  selectcolumns: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m6 2c-1.09306 0-2 .90694-2 2v16c0 1.0931.90694 2 2 2h4c1.0931 0 2-.9069 2-2v-16c0-1.09306-.9069-2-2-2zm8 0v2h4v4h-4v2h4v4h-4v2h4v4h-4v2h4c1.0931 0 2-.9069 2-2v-16c0-1.09306-.9069-2-2-2zm-8 2h4v4h-4zm0 6h4v4h-4zm0 6h4v4h-4z",
      },
    ],
  },
  editcolumns: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m9 2c-1.103 0-2 .897-2 2v16c0 1.103.897 2 2 2h6c1.103 0 2-.897 2-2v-2.1719l-2-2v4.1719h-6v-16h6v2.17188l2 2v-4.17188c0-1.103-.897-2-2-2zm4 7v2l5.1465 5.1465 2-2-5.1465-5.1465zm7.8535 5.8535-2 2 1 1c.195.195.512.195.707 0l1.293-1.293c.195-.196.195-.512 0-.707z",
      },
    ],
  },
  append: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "M11 2V5H8L12 9L16 5H13V2H11ZM12 9H3V11H21V9H12ZM3 13V15H12H21V13H3ZM12 15L8 19H11V22H13V19H16L12 15Z",
      },
    ],
  },
  newtable: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m5 2c-1.103 0-2 .897-2 2v16c0 1.103.897 2 2 2h7.6836c-.296-.622-.5826-1.293-.6836-2v-4h.6836c.356-.749.8359-1.426 1.4219-2h-2.1055v-4h5v2.2949c.634-.189 1.304-.2949 2-.2949v-8c0-1.103-.897-2-2-2zm0 2h5v4h-5zm7 0h5v4h-5zm-7 6h5v4h-5zm14 4c-2.761 0-5 2.239-5 5s2.239 5 5 5 5-2.239 5-5-2.239-5-5-5zm-14 2h5v4h-5zm13 0h2v2h2v2h-2v2h-2v-2h-2v-2h2z",
      },
    ],
  },
  import_file: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m6 2c-1.09425 0-2 .90575-2 2v16c0 1.0943.90575 2 2 2h12c1.0943 0 2-.9057 2-2v-12l-6-6zm0 2h7v5h5v11h-12zm2 8v2h8v-2zm0 4v2h8v-2z",
      },
    ],
  },
  sort: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m7 2-5 5h4v6h2v-6h4zm9 1v2h2v-2zm0 4v2h2v-2zm0 4v6h-4l5 5 5-5h-4v-6zm-10 4v2h2v-2zm0 4v2h2v-2z",
      },
    ],
  },
  import_extract: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m11 3v12h-2l3 3 3-3h-2v-12zm-7.29297.29297-1.41406 1.41406 2.70703 2.70703v3.58594h-2l3 3 3-3h-2v-4.41406zm16.58597 0-3.293 3.29297v4.41406h-2l3 3 3-3h-2v-3.58594l2.707-2.70703zm-18.293 13.70703v3c0 1.0931.90694 2 2 2h16c1.0931 0 2-.9069 2-2v-3h-2v3h-16v-3z",
      },
    ],
  },
  standardize: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m17 2v11.1445c-1.7158.4499-3 2.0052-3 3.8555v1.9277c0 .7143-.2007.792-.459.9258-.2582.1338-.541.1465-.541.1465v2h6c1.645 0 3-1.355 3-3v-2c0-1.8503-1.2842-3.4056-3-3.8555v-11.1445zm-11.31055.97656-1.70898 1.03906 2.41992 3.98438h-3.58203l2.33398 14h5.84766v-2h-4.15234l-1.66602-10h2.43359l1.5293 2.5195h.00195c.08912.1466.21444.2678.36391.3519.14948.0842.31808.1284.48961.1286.2652 0 .5196-.1054.7071-.2929s.2929-.4419.2929-.7071c-.0003-.1833-.051-.3631-.1465-.5195h.002zm3.81055.02344c-.458 0-.88295.13266-1.25195.34766l2.22075 3.65234h4.4804c.032-.162.0508-.329.0508-.5 0-1.381-1.119-2.5-2.5-2.5-.315 0-.6136.06588-.8906.17188-.443-.70201-1.2184-1.17188-2.1094-1.17188zm1.5645 5 1.2148 2h1.541l-.5547 3.3281c.611-.786 1.4091-1.4162 2.3321-1.8222l.5839-3.5059zm6.9355 7c1.1167 0 2 .8833 2 2v2c0 .565-.435 1-1 1h-3.3086c.13-.3412.3086-.6477.3086-1.0723v-1.9277c0-1.1167.8833-2 2-2z",
      },
    ],
  },
  sql: {
    viewBox: "0 0 24 24",
    shapes: [
      {
        d: "m9.00195 11.8223c.886-.019.99805 1.0668.99805 1.2988v.8164c0 .221-.16628 1.2272-.98828 1.2422-.857.017-1.01172-1.0202-1.01172-1.2422v-.8164c0-.232.05695-1.2798 1.00195-1.2988z",
      },
      {
        d: "m4 4c0-1.09425.90575-2 2-2h9l5 5v13c0 1.0943-.9057 2-2 2h-12c-1.09425 0-2-.9057-2-2v-2c-1.105 0-2-.895-2-2v-5c0-1.105.895-2 2-2zm10 0h-8v5h8c1.105 0 2 .895 2 2v5c0 1.105-.895 2-2 2h-8v2h12v-12h-4zm-4.99997 6.9999c-1.833.029-2 1.7789-2 2.1289l-.00001.8223c-.00049.3746-.00259 2.0035 2.00782 2.0449.3485.0077.61801-.0593.85547-.1524l.68949 1.3789.8945-.4453-.7891-1.5801c.3089-.4995.3418-1.0785.3418-1.2597v-.8086c0-.35-.136-2.1589-1.99997-2.1289zm2.99997.0001v5h3v-1h-2v-4zm-7.35547.0019c-.894-.025-1.53839.4823-1.52539 1.3593.01388.9012.71894 1.2316 1.23616 1.474.04178.0196.08239.0386.12126.0573.37.178.71927.3977.69727.7637-.014.218-.10277.5109-.50977.5449-.677.055-.68945-.6558-.68945-.8028h-.97461c0 .277.10506 1.6186 1.66406 1.6016 1.433-.015 1.48833-1.1356 1.48633-1.3496-.009-1.008-1.11584-1.502-1.33984-1.584-.00824-.0037-.0193-.0083-.03272-.014-.161-.0677-.66122-.2782-.68799-.6852-.03-.483.36407-.5606.53907-.5606.56127 0 .55909.5662.55861.6904l-.00002.0089h.9668c0-.221-.08677-1.4629-1.50977-1.5039z",
        evenOdd: true,
      },
    ],
  },
};

interface CascadeOperationGlyphProps {
  /**
   * The engine's id for what the step did, or null where the step's record
   * carries none.
   */
  operation: string | null | undefined;
  /** Sizing and colour, from the tile the glyph sits in. */
  className?: string;
}

/**
 * The picture for one step's operation, or the neutral mark for an operation
 * this build holds no picture for.
 *
 * **An unrecognised id is the ordinary case.** Cascade declares around twice the
 * operations it draws glyphs for and is free to declare more without this build
 * hearing of it, so the lookup degrades to a mark that says only "a step ran
 * here" — never a blank tile, never a throw, and never a guess at which
 * neighbouring operation was meant. Nothing a row means rests on the glyph: the
 * name beside it is what says what the step did.
 *
 * Always `aria-hidden`. The glyph restates the operation, which the step's name
 * and its card already carry, and announcing it would read a second label over
 * every row for a picture that adds nothing to either.
 */
export function CascadeOperationGlyph({
  operation,
  className,
}: CascadeOperationGlyphProps) {
  // An own-property check, because the ids are the engine's and open-ended: one
  // spelled like a member of `Object.prototype` would index to a truthy
  // non-glyph and throw on render, which no boundary above here catches.
  const glyphKey = operation == null ? null : normalisedOperation(operation);
  const glyph =
    glyphKey !== null && Object.prototype.hasOwnProperty.call(GLYPHS, glyphKey)
      ? GLYPHS[glyphKey]
      : undefined;

  if (glyph === undefined) {
    return (
      <svg
        viewBox="0 0 24 24"
        className={className}
        aria-hidden
        focusable="false"
      >
        <circle
          cx="12"
          cy="12"
          r="7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox={glyph.viewBox}
      className={className}
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      {glyph.shapes.map((shape) => (
        <path
          key={shape.d}
          d={shape.d}
          fillRule={shape.evenOdd ? "evenodd" : undefined}
          clipRule={shape.evenOdd ? "evenodd" : undefined}
        />
      ))}
    </svg>
  );
}
